import React, { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, Files, Share2, Settings, HardDrive, Trash, Users } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import ProfileAvatar from './ProfileAvatar'

interface SidebarProps {
  username: string
  storageUsed: number
  storageLimit: number
}

const Sidebar: React.FC<SidebarProps> = ({ username, storageUsed, storageLimit }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  
  const storagePercentage = (storageUsed / storageLimit) * 100
  const formattedStorageUsed = (storageUsed / (1024 * 1024 * 1024)).toFixed(2)
  const formattedStorageLimit = (storageLimit / (1024 * 1024 * 1024)).toFixed(0)
  
  const isActive = (path: string) => {
    return location.pathname.includes(path)
  }
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])
  
  return (
    <div 
      className={`bg-white dark:bg-[#2a2a2a] h-full transition-all duration-300 shadow-custom-light flex flex-col ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="flex justify-end p-2">
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-full hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)]"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        <nav className="px-2 py-4">
          <ul className="space-y-2">
            <li>
              <button
                onClick={() => navigate('/portal/files')}
                className={`flex items-center w-full p-3 rounded-lg transition-colors ${
                  isActive('/files') && !isActive('/trash')
                    ? 'bg-[var(--color-primary)] text-[var(--color-background)]' 
                    : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)]'
                }`}
              >
                <Files size={20} />
                {!collapsed && <span className="ml-3 truncate">Files</span>}
              </button>
            </li>
            <li>
              <button
                onClick={() => navigate('/portal/shared')}
                className={`flex items-center w-full p-3 rounded-lg transition-colors ${
                  isActive('/shared') 
                    ? 'bg-[var(--color-primary)] text-[var(--color-background)]' 
                    : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)]'
                }`}
              >
                <Share2 size={20} />
                {!collapsed && <span className="ml-3 truncate">Shared Links</span>}
              </button>
            </li>
            <li>
              <button
                onClick={() => navigate('/portal/trash')}
                className={`flex items-center w-full p-3 rounded-lg transition-colors ${
                  isActive('/trash') 
                    ? 'bg-[var(--color-primary)] text-[var(--color-background)]' 
                    : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)]'
                }`}
              >
                <Trash size={20} />
                {!collapsed && <span className="ml-3 truncate">Trash</span>}
              </button>
            </li>
            <li>
              <button
                onClick={() => navigate('/portal/settings')}
                className={`flex items-center w-full p-3 rounded-lg transition-colors ${
                  isActive('/settings') 
                    ? 'bg-[var(--color-primary)] text-[var(--color-background)]' 
                    : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)]'
                }`}
              >
                <Settings size={20} />
                {!collapsed && <span className="ml-3 truncate">Settings</span>}
              </button>
            </li>
            {user?.isAdmin && (
              <li>
                <button
                  onClick={() => navigate('/portal/admin')}
                  className={`flex items-center w-full p-3 rounded-lg transition-colors ${
                    isActive('/admin') 
                      ? 'bg-[var(--color-primary)] text-[var(--color-background)]' 
                      : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)]'
                  }`}
                >
                  <Users size={20} />
                  {!collapsed && <span className="ml-3 truncate">Administration</span>}
                </button>
              </li>
            )}
          </ul>
        </nav>
      </div>
      
      {!collapsed && (
        <div className="p-4 border-t border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30 mt-auto">
          <div className="mb-2 relative" ref={dropdownRef}>
            <div className="flex items-center space-x-3">
              <ProfileAvatar user={user || { id: 'unknown', username }} size="sm" />
              <button 
                className="text-sm font-medium text-[var(--color-primary)] hover:underline focus:outline-none truncate flex-grow text-left"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {username}
              </button>
            </div>
            
            {dropdownOpen && (
              <div className="absolute bottom-full left-0 mb-2 w-full bg-white dark:bg-[#2a2a2a] shadow-custom-medium dark:shadow-lg dark:shadow-black/30 rounded-lg z-20 border border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30">
                <div className="p-3">
                  <button 
                    className="w-full text-left text-sm text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] p-2 rounded"
                    onClick={() => {
                      navigate('/portal/settings')
                      setDropdownOpen(false)
                    }}
                  >
                    Profile Settings
                  </button>
                  <button 
                    className="w-full text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded mt-1"
                    onClick={() => {
                      // Logout logic would go here
                      navigate('/login')
                      setDropdownOpen(false)
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center text-sm text-[var(--color-accent)]">
              <HardDrive size={16} className="mr-2" />
              <span>{formattedStorageUsed} GB / {formattedStorageLimit} GB</span>
            </div>
            <div className="w-full bg-[var(--color-background)] dark:bg-[#1f1f1f] rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  storagePercentage > 90 
                    ? 'bg-red-500' 
                    : storagePercentage > 70 
                      ? 'bg-yellow-500' 
                      : 'bg-[var(--color-secondary)]'
                }`}
                style={{ width: `${storagePercentage}%` }}
              ></div>
            </div>
          </div>
          <div className="mt-8 text-xs text-[var(--color-accent)] opacity-40">
            ©2025 Stefan Stöger
          </div>
        </div>
      )}
    </div>
  )
}

export default Sidebar